import { computed, inject, Injectable, signal } from '@angular/core';
import { isActive, IsActiveMatchOptions, Router } from '@angular/router';
import { AccountService } from '@core/auth';
import { FinanceInvoiceTaskService } from '@shared/data/finance-invoice-task.service';
import { IncidentTaskCountService } from '@shared/data/incident-task-count.service';
import { InvoiceTaskSummaryService } from '@shared/data/invoice-task-summary.service';

export type NavigationItem = {
  id: string;
  icon: string;
  tooltip: string;
  route?: string;
  label?: string;
  routeActiveOptions?: { exact: boolean };
  badge?: string;
  items?: NavigationItem[];
};

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private financeInvoiceTaskService = inject(FinanceInvoiceTaskService);
  private incidentTaskCountService = inject(IncidentTaskCountService);
  private invoiceTaskSummaryService = inject(InvoiceTaskSummaryService);
  private accountService = inject(AccountService);
  private router = inject(Router);

  readonly selectedItemId = signal<string | null>(null);

  readonly navigation = computed<NavigationItem[]>(() => {
    const count = this.financeInvoiceTaskService.count();
    // Presentation only; the backend's `aiUser` middleware enforces access. Hidden until resolved.
    const isAiUser = this.accountService.profile()?.isAiUser === true;

    /**
     * Incidents are hidden from employees who do no property management, and
     * from property managers who are not in the beta group, for as long as the
     * feature runs in parallel with the legacy portal.
     */
    const showIncidents = this.accountService.canAccessIncidents();

    // The rail shows one entry for tasks, so its badge is every kind of task
    // waiting; the panel underneath splits them again per feature.
    const incidentCount = showIncidents
      ? this.incidentTaskCountService.count()
      : 0;
    const showInvoices = this.accountService.canAccessInvoices();
    const invoiceCount = showInvoices
      ? this.invoiceTaskSummaryService.summary().total
      : 0;
    const tasksCount = count + invoiceCount + incidentCount;

    return [
      {
        id: 'dashboard',
        icon: 'layout-dashboard',
        route: '/dashboard',
        tooltip: 'LAYOUT.DASHBOARD',
      },
      ...(isAiUser
        ? [
            {
              id: 'assistant',
              icon: 'sparkles',
              route: '/assistant',
              tooltip: 'LAYOUT.ASSISTANT',
              routeActiveOptions: { exact: false },
            },
          ]
        : []),
      {
        id: 'search',
        icon: 'search',
        tooltip: 'LAYOUT.SEARCH',
        routeActiveOptions: { exact: false },
        items: [
          {
            id: 'finance-invoices',
            icon: 'briefcase-business',
            label: 'LAYOUT.FINANCE_INVOICES',
            tooltip: 'LAYOUT.FINANCE_INVOICES',
            route: '/finance-invoices',
            routeActiveOptions: { exact: false },
          },
          ...(showInvoices
            ? [
                {
                  id: 'property-management-invoice-search',
                  icon: 'building-2',
                  label: 'LAYOUT.PROPERTY_MANAGEMENT_INVOICE_SEARCH',
                  tooltip: 'LAYOUT.PROPERTY_MANAGEMENT_INVOICE_SEARCH',
                  route: '/property-management-invoice-search',
                  routeActiveOptions: { exact: false },
                },
              ]
            : []),
          ...(showIncidents
            ? [
                {
                  id: 'incident-search',
                  icon: 'wrench',
                  label: 'LAYOUT.INCIDENT_SEARCH',
                  tooltip: 'LAYOUT.INCIDENT_SEARCH',
                  route: '/incident-search',
                  routeActiveOptions: { exact: false },
                },
                {
                  id: 'works-history',
                  icon: 'hammer',
                  label: 'LAYOUT.WORKS_HISTORY',
                  tooltip: 'LAYOUT.WORKS_HISTORY',
                  route: '/works-history',
                  routeActiveOptions: { exact: false },
                },
              ]
            : []),
          ...(this.accountService.canAccessCgiHistory()
            ? [
                {
                  id: 'cgi-history',
                  icon: 'archive',
                  label: 'LAYOUT.CGI_HISTORY',
                  tooltip: 'LAYOUT.CGI_HISTORY',
                  route: '/cgi-history',
                  routeActiveOptions: { exact: false },
                },
              ]
            : []),
        ],
      },
      {
        id: 'tasks',
        icon: 'list-todo',
        tooltip: 'LAYOUT.TASKS',
        badge: tasksCount > 0 ? String(tasksCount) : undefined,
        routeActiveOptions: { exact: false },
        items: [
          {
            id: 'finance-invoice-tasks',
            icon: 'briefcase-business',
            label: 'LAYOUT.FINANCE_INVOICE_TASKS',
            tooltip: 'LAYOUT.FINANCE_INVOICE_TASKS',
            route: '/finance-invoice-tasks',
            routeActiveOptions: { exact: false },
            badge: count > 0 ? String(count) : undefined,
          },
          ...(showInvoices
            ? [
                {
                  id: 'property-management-invoice-tasks',
                  icon: 'building-2',
                  label: 'LAYOUT.PROPERTY_MANAGEMENT_INVOICE_TASKS',
                  tooltip: 'LAYOUT.PROPERTY_MANAGEMENT_INVOICE_TASKS',
                  route: '/property-management-invoices',
                  routeActiveOptions: { exact: false },
                  badge: invoiceCount > 0 ? String(invoiceCount) : undefined,
                },
              ]
            : []),
          ...(showIncidents
            ? [
                {
                  id: 'incident-tasks',
                  icon: 'wrench',
                  label: 'LAYOUT.INCIDENT_TASKS',
                  tooltip: 'LAYOUT.INCIDENT_TASKS',
                  // The area, not the first tab: `/incidents` is a subset of
                  // both tab URLs, so the pool keeps the entry lit — and the
                  // redirect lands the click on my tasks either way.
                  route: '/incidents',
                  routeActiveOptions: { exact: false },
                  badge: incidentCount > 0 ? String(incidentCount) : undefined,
                },
              ]
            : []),
        ],
      },
      ...(this.accountService.canAccessApplicationOffers()
        ? [
            {
              id: 'application-offers',
              icon: 'megaphone',
              route: '/application-offers',
              tooltip: 'LAYOUT.APPLICATION_OFFERS',
              routeActiveOptions: { exact: false },
            },
          ]
        : []),
      {
        id: 'settings',
        icon: 'settings',
        route: '/settings',
        tooltip: 'LAYOUT.SETTINGS',
        routeActiveOptions: { exact: false },
      },
    ];
  });

  readonly selectedNavItem = computed(() => {
    const id = this.selectedItemId();
    if (!id) return null;
    return this.navigation().find((item) => item.id === id) ?? null;
  });

  readonly selectedItems = computed(() => {
    return this.selectedNavItem()?.items ?? null;
  });

  togglePanel(id: string) {
    this.selectedItemId.update((current) => (current === id ? null : id));
  }

  closePanel() {
    this.selectedItemId.set(null);
  }

  isItemActive(item: NavigationItem) {
    if (!item.items?.length) return false;
    const matchOptions: IsActiveMatchOptions = {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    };
    return item.items.some(
      (child) =>
        child.route && isActive(child.route, this.router, matchOptions)()
    );
  }
}
